import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Globe,
  KeyRound,
  Loader2,
  Plus,
  UserMinus,
  Users,
  UsersRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { ApiError } from '../lib/api';
import {
  miembrosApi,
  miembrosKeys,
  type GrupoResumen,
  type Miembro,
  type RolMembresia,
} from '../lib/miembros';

/** Los 4 roles del enum, con su explicación de una línea (title del select). */
const ROLES: { valor: RolMembresia; texto: string; titulo: string }[] = [
  { valor: 'facilitador', texto: 'Facilitador', titulo: 'Conduce las sesiones del proceso' },
  { valor: 'decisor', texto: 'Decisor', titulo: 'Decide y también puede conducir' },
  { valor: 'participante', texto: 'Participante', titulo: 'Aporta ideas y evidencia en las sesiones' },
  { valor: 'observador', texto: 'Observador', titulo: 'Solo mira, no aporta' },
];

/** Origen de identidad → icono chico con explicación. */
const PROVEEDORES: Record<string, { icono: LucideIcon; titulo: string }> = {
  sala: { icono: UsersRound, titulo: 'Entró por código de sala (auto-registro)' },
  google: { icono: Globe, titulo: 'Entró con su cuenta de Google' },
  local: { icono: KeyRound, titulo: 'Entró con correo y contraseña' },
};

/** Piel común de los selects densos de la tabla (tokens, sin hex). */
const selectClase =
  'min-h-touch w-full rounded-md border border-border-strong bg-surface-raised px-2 ' +
  'font-sans text-sm text-content transition-colors duration-fast ' +
  'focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50';

/**
 * Participantes (/participantes/:procesoId) — el gestor de miembros del
 * proceso: grupos, roles, asignación y bajas. Poll suave: los auto-registrados
 * por sala van apareciendo mientras la mirás.
 */
export function ParticipantesPage() {
  useSurfaceTheme('cabina', 'dark');
  const { procesoId = '' } = useParams<{ procesoId: string }>();

  const miembros = useQuery({
    queryKey: miembrosKeys.lista(procesoId),
    queryFn: () => miembrosApi.listar(procesoId),
    enabled: Boolean(procesoId),
    refetchInterval: 15_000,
  });

  const grupos = useQuery({
    queryKey: miembrosKeys.grupos(procesoId),
    queryFn: () => miembrosApi.grupos(procesoId),
    enabled: Boolean(procesoId),
    refetchInterval: 15_000,
  });

  const lista = miembros.data ?? [];
  const listaGrupos = grupos.data ?? [];

  return (
    <div className="flex min-h-screen flex-col bg-surface-base">
      {/* Barra superior: volver a la portada del conductor */}
      <header className="flex items-center justify-between border-b border-border px-6 py-3">
        <div className="flex items-center gap-4">
          <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-7 w-auto" />
          <Link
            to="/procesos"
            className="inline-flex min-h-touch items-center gap-2 rounded-md px-2 font-sans text-sm font-semibold text-content-muted transition-colors duration-fast hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Volver a procesos
          </Link>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-4 px-6 py-6">
        <h1 className="flex items-center gap-2 font-display text-2xl font-semibold text-content">
          <Users className="h-6 w-6 text-primary" aria-hidden="true" />
          Participantes
        </h1>

        {/* GRUPOS: chips con conteo + alta compacta */}
        <Card title="Grupos">
          <ZonaGrupos procesoId={procesoId} grupos={listaGrupos} cargando={grupos.isLoading} />
        </Card>

        {/* MIEMBROS: tabla densa con rol, grupo y baja */}
        <Card title="Miembros">
          {miembros.isLoading ? (
            <p className="flex items-center gap-2 font-sans text-sm text-content-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Cargando participantes…
            </p>
          ) : miembros.isError ? (
            <p className="font-sans text-sm text-error">
              No se pudieron cargar los participantes. Revisá tu conexión e intentá de nuevo.
            </p>
          ) : lista.length === 0 ? (
            <p className="font-sans text-sm text-content-muted">
              Todavía no hay nadie en el proceso. Compartí el código de sala y los
              participantes van a ir apareciendo acá solos.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse font-sans text-sm">
                <thead>
                  <tr className="border-b border-border-strong text-left text-xs uppercase tracking-wide text-content-muted">
                    <th className="px-2 py-2 font-semibold">Nombre</th>
                    <th className="px-2 py-2 font-semibold">Rol</th>
                    <th className="px-2 py-2 font-semibold">Grupo</th>
                    <th className="px-2 py-2 font-semibold">
                      <span className="sr-only">Quitar</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((m) => (
                    <FilaMiembro
                      key={m.user_id}
                      procesoId={procesoId}
                      miembro={m}
                      grupos={listaGrupos}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Pie de contexto (C1) */}
        <p className="font-sans text-xs text-content-muted">
          Quitar a alguien no borra sus aportes: la evidencia ya aportada queda en
          las actas (C1).
        </p>
      </main>
    </div>
  );
}

/* ------------------------------ Grupos --------------------------------- */

function ZonaGrupos({
  procesoId,
  grupos,
  cargando,
}: {
  procesoId: string;
  grupos: GrupoResumen[];
  cargando: boolean;
}) {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');

  const crearMut = useMutation({
    mutationFn: (n: string) => miembrosApi.crearGrupo(procesoId, n),
    onSuccess: () => {
      setNombre('');
      void qc.invalidateQueries({ queryKey: miembrosKeys.grupos(procesoId) });
      void qc.invalidateQueries({ queryKey: miembrosKeys.lista(procesoId) });
    },
  });

  // El 409 del backend ya viene humano («Ya existe un grupo con ese nombre…»).
  const crearError = (crearMut.error as ApiError | null)?.detail ?? null;

  function onCrear() {
    const limpio = nombre.trim();
    if (!limpio || crearMut.isPending) return;
    crearMut.mutate(limpio);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {cargando ? (
          <p className="flex items-center gap-2 font-sans text-sm text-content-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Cargando grupos…
          </p>
        ) : grupos.length === 0 ? (
          <p className="font-sans text-sm text-content-muted">
            Sin grupos todavía. Creá el primero para repartir a la gente en mesas.
          </p>
        ) : (
          grupos.map((g) => (
            <Badge key={g.id} variant="neutral">
              {g.nombre} · {g.n_miembros}
            </Badge>
          ))
        )}
      </div>

      <div className="flex max-w-md items-center gap-2">
        <input
          type="text"
          value={nombre}
          onChange={(e) => {
            setNombre(e.target.value);
            if (crearMut.isError) crearMut.reset();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCrear();
            }
          }}
          placeholder="Nuevo grupo (p. ej. Mesa 1)"
          aria-label="Nombre del nuevo grupo"
          className={
            'min-h-touch w-full rounded-md border bg-surface-raised px-3 font-sans text-sm text-content ' +
            'placeholder:text-content-muted transition-colors duration-fast ' +
            'focus-visible:outline-none focus-visible:shadow-focus ' +
            (crearError ? 'border-error' : 'border-border-strong')
          }
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={onCrear}
          loading={crearMut.isPending}
          disabled={!nombre.trim()}
          leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
        >
          Crear
        </Button>
      </div>

      {crearError && (
        <p role="alert" className="flex items-center gap-1 font-sans text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {crearError}
        </p>
      )}
    </div>
  );
}

/* ----------------------------- Miembros -------------------------------- */

function FilaMiembro({
  procesoId,
  miembro,
  grupos,
}: {
  procesoId: string;
  miembro: Miembro;
  grupos: GrupoResumen[];
}) {
  const qc = useQueryClient();
  const [confirmando, setConfirmando] = useState(false);

  // El primer tap de «quitar» expira solo si no llega el segundo.
  useEffect(() => {
    if (!confirmando) return;
    const t = window.setTimeout(() => setConfirmando(false), 4000);
    return () => window.clearTimeout(t);
  }, [confirmando]);

  function invalidar() {
    void qc.invalidateQueries({ queryKey: miembrosKeys.lista(procesoId) });
    void qc.invalidateQueries({ queryKey: miembrosKeys.grupos(procesoId) });
  }

  const editarMut = useMutation({
    mutationFn: (cuerpo: { rol?: RolMembresia; grupo_id?: string | null }) =>
      miembrosApi.editar(procesoId, miembro.user_id, cuerpo),
    onSuccess: invalidar,
  });

  const quitarMut = useMutation({
    mutationFn: () => miembrosApi.quitar(procesoId, miembro.user_id),
    onSuccess: invalidar,
  });

  // Mismo patrón que accionError de la Cabina: detail humano de la API.
  const accionError =
    (editarMut.error as ApiError | null)?.detail ??
    (quitarMut.error as ApiError | null)?.detail ??
    null;

  const ocupado = editarMut.isPending || quitarMut.isPending;
  const rolActual = ROLES.find((r) => r.valor === miembro.rol);

  return (
    <>
      <tr className="border-b border-border">
        <td className="px-2 py-2">
          <span className="flex items-center gap-2">
            <span className="font-medium text-content">{miembro.nombre}</span>
            {miembro.proveedores.map((p) => {
              const prov = PROVEEDORES[p];
              if (!prov) return null;
              const Icono = prov.icono;
              return (
                <span
                  key={p}
                  title={prov.titulo}
                  className="inline-flex items-center rounded-full border border-border bg-surface-sunken p-1 text-content-muted"
                >
                  <Icono className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="sr-only">{prov.titulo}</span>
                </span>
              );
            })}
          </span>
          {miembro.email && (
            <span className="block text-xs text-content-muted">{miembro.email}</span>
          )}
        </td>
        <td className="px-2 py-2">
          <select
            value={miembro.rol}
            disabled={ocupado}
            title={rolActual?.titulo}
            aria-label={`Rol de ${miembro.nombre}`}
            onChange={(e) => editarMut.mutate({ rol: e.target.value as RolMembresia })}
            className={selectClase}
          >
            {ROLES.map((r) => (
              <option key={r.valor} value={r.valor} title={r.titulo}>
                {r.texto}
              </option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2">
          <select
            value={miembro.grupo_id ?? ''}
            disabled={ocupado}
            aria-label={`Grupo de ${miembro.nombre}`}
            onChange={(e) => editarMut.mutate({ grupo_id: e.target.value || null })}
            className={selectClase}
          >
            <option value="">Sin grupo</option>
            {grupos.map((g) => (
              <option key={g.id} value={g.id}>
                {g.nombre}
              </option>
            ))}
          </select>
        </td>
        <td className="px-2 py-2 text-right">
          {confirmando ? (
            <Button
              variant="danger"
              size="sm"
              loading={quitarMut.isPending}
              onClick={() => quitarMut.mutate()}
              leadingIcon={<UserMinus className="h-4 w-4" aria-hidden="true" />}
              title={`Segundo toque: quitar a ${miembro.nombre} del proceso`}
            >
              ¿Quitar?
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={ocupado}
              onClick={() => {
                quitarMut.reset();
                setConfirmando(true);
              }}
              leadingIcon={<UserMinus className="h-4 w-4" aria-hidden="true" />}
              title={`Quitar a ${miembro.nombre} del proceso (sus aportes quedan en las actas)`}
            >
              Quitar
            </Button>
          )}
        </td>
      </tr>
      {accionError && (
        <tr>
          <td colSpan={4} className="px-2 pb-2">
            <p role="alert" className="flex items-center gap-1 font-sans text-sm text-error">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              {accionError}
            </p>
          </td>
        </tr>
      )}
    </>
  );
}
